import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { DataTable, Column, SCIcon, Dialog, ProgressBar, ReactCountryFlag } from '../../Common/Imports';
import { ApiResponse, Brands_EXT, Contacts_EXT, Countries_EXT, DDModel } from '../../Models/Classes';
import { ContactType } from '../../Models/Enums';
import ContactEdit from './Contacts_Edit';
interface StakeholderContactsProps {
    stakeholderId: number;
}

export const ContactsList: React.FC<StakeholderContactsProps> = ({ stakeholderId }) => {
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<Contacts_EXT[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [ShowEdit, setShowEdit] = useState<boolean>(false);
    const [EditId, setEditId] = useState<number | undefined>(undefined);
    const [CountriesData, setCountriesData] = useState<Countries_EXT[] | undefined>();
    const [BrandsData, setBrandsData] = useState<DDModel<Brands_EXT>[] | undefined>();

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<Contacts_EXT[]>>(`Contacts/GetStakeholderContactsList/${stakeholderId}`);
            if (resp.data.code == 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    created: item.created ? new Date(item.created) : undefined
                }));
                SetTableData(processedData);
            }
            const resp2 = await AxiosHelper.get<ApiResponse<Countries_EXT[]>>(`Countries/GetList`);
            if (resp2.data.code === 0) {
                setCountriesData(resp2.data.data);
            }
            const resp3 = await AxiosHelper.get<ApiResponse<DDModel<Brands_EXT>[]>>(`DropDown/StakeholderBrands?IdStakeholder=${stakeholderId}`);
            if (resp3.data.code === 0) {
                setBrandsData(resp3.data.data);
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, showToast, stakeholderId, t]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const updateStatus = async (data: Contacts_EXT) => {
        data.isActive = !data.isActive;
        try {
            const resp = await AxiosHelper.post<ApiResponse<Contacts_EXT>>(`Contacts/UpdateContact/`, data);
            if (resp.data.code == 0) {
                showToast('success', 'Success', t("Common.Toasts.New.Success"));
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            GetData();
        }
    }

    const activeBody = (data: Contacts_EXT) => {
        return (
            !data.isActive ?
                <SCIcon Name="Lock" Classes="text-danger" Pointer OnClick={() => updateStatus(data)} />
                :
                <SCIcon Name="Unlock" Classes="text-success" Pointer OnClick={() => updateStatus(data)} />
        );
    }
    const typeBody = (data: Contacts_EXT) => {
        if (data.type === undefined) {
            return null;
        }
        return <><SCIcon Name={ContactType[data.type]} /></>
    }

    const editBody = (data: Contacts_EXT) => {
        return <SCIcon Name="Edit" Classes="text-secondary" Pointer OnClick={() => { setEditId(data.idContact); setShowEdit(true) }} />;
    }

    const deleteRecord = async (data: Contacts_EXT) => {
        SetTableLoader(true);
        try {

            const resp = await AxiosHelper.post<ApiResponse<Contacts_EXT[]>>(`Contacts/DeleteContact`, data);
            if (resp.data.code == 0) {
                showToast('success', 'Success', t("Common.Toasts.Delete.Success"));
                GetData();
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            SetTableLoader(false);
        }
    }

    const deleteBody = (data: Contacts_EXT) => {
        return <SCIcon Name="Delete" Classes="text-danger" Pointer OnClick={() => deleteRecord(data)} />;
    }

    return (
        <>
            {
                ShowEdit &&
                <Dialog
                    header={EditId ? "Edit Contact" : "Create Contact"}
                    visible={true}
                    onHide={() => { setShowEdit(false); GetData() }}>
                    <ContactEdit stakeholderId={stakeholderId} contactId={EditId} OnSave={() => { setShowEdit(false); GetData() }} />
                </Dialog>
            }
            <div className="col d-flex justify-content-end">
                <SCIcon Name="Add" Classes="text-primary" Pointer OnClick={() => { setEditId(undefined); setShowEdit(true) }}></SCIcon>
            </div>
            {
                TableLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <DataTable
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        value={TableData}
                        size="small"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        stripedRows>
                        <Column
                            field="type"
                            body={typeBody}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="value"
                            bodyClassName="table-cell-nowrap" />
                        <Column field="idCountry"
                            header={t("Contacts.Location")}
                            body={(e: Contacts_EXT) => {
                                const country = CountriesData?.find(m => m.idCountry === e.idCountry);
                                return <><ReactCountryFlag
                                    countryCode={country?.isO2 ?? ""}
                                    svg
                                    title=""
                                    style={{ marginRight: "10px" }} />
                                    {country?.name || ""}</>;
                            }}
                            bodyClassName="table-cell-nowrap" />

                        <Column field="idCountry"
                            header={t("Contacts.Brand")}
                            body={(e: Contacts_EXT) => {
                                const brands = BrandsData?.find(m => m.id === e.idBrand);
                                return <>{brands?.label}</>;
                            }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="isActive"
                            header={t("Contacts.Status")}
                            style={{ maxWidth: '150px!important', width: "150px" }}
                            body={(e: Contacts_EXT) => { const _T = e.isActive ? t("Common.Booleans.Active") : t("Common.Booleans.NotActive"); return _T }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="active"
                            body={(e: Contacts_EXT) => activeBody(e)} style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            body={(e: Contacts_EXT) => deleteBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            body={(e: Contacts_EXT) => editBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                    </DataTable>
            }
        </>
    );
};

export default ContactsList;
